import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { DeckCard } from '@/hooks/useDecks';

/** A compact, restore-friendly representation of a deck's cards at a point in time. */
export interface DeckSnapshotPayload {
  cards: { card_id: string; quantity: number }[];
}

export interface DeckSnapshot {
  id: string;
  deck_id: string;
  user_id: string;
  label: string;
  snapshot_json: DeckSnapshotPayload;
  created_at: string;
}

const MAX_KEPT = 20; // most-recent N per deck

export function useDeckSnapshots(deckId: string | null) {
  return useQuery({
    queryKey: ['deck-snapshots', deckId],
    queryFn: async () => {
      if (!deckId) return [];
      const { data, error } = await supabase
        .from('deck_snapshots' as any)
        .select('*')
        .eq('deck_id', deckId)
        .order('created_at', { ascending: false })
        .limit(MAX_KEPT);
      if (error) throw error;
      return (data || []) as unknown as DeckSnapshot[];
    },
    enabled: !!deckId,
  });
}

export function useCreateDeckSnapshot() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      deckId,
      label,
      cards,
    }: {
      deckId: string;
      label: string;
      cards: DeckCard[];
    }) => {
      if (!user) throw new Error('Not authenticated');
      const payload: DeckSnapshotPayload = {
        cards: cards.map(c => ({ card_id: c.card_id, quantity: c.quantity })),
      };
      const { error } = await supabase.from('deck_snapshots' as any).insert({
        deck_id: deckId,
        user_id: user.id,
        label,
        snapshot_json: payload as any,
      });
      if (error) throw error;
      // Best-effort prune: keep only the latest MAX_KEPT for this deck.
      const { data: extras } = await supabase
        .from('deck_snapshots' as any)
        .select('id, created_at')
        .eq('deck_id', deckId)
        .order('created_at', { ascending: false });
      if (extras && extras.length > MAX_KEPT) {
        const toDelete = (extras as any[]).slice(MAX_KEPT).map(r => r.id);
        await supabase.from('deck_snapshots' as any).delete().in('id', toDelete);
      }
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['deck-snapshots', vars.deckId] }),
  });
}

export function useDeleteDeckSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, deckId: _d }: { id: string; deckId: string }) => {
      const { error } = await supabase.from('deck_snapshots' as any).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['deck-snapshots', vars.deckId] }),
  });
}

/** Replace the deck's cards entirely from a snapshot payload. */
export function useRestoreDeckSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      deckId,
      payload,
    }: {
      deckId: string;
      payload: DeckSnapshotPayload;
    }) => {
      // Wipe current cards, then bulk insert.
      const { error: delErr } = await supabase.from('deck_cards').delete().eq('deck_id', deckId);
      if (delErr) throw delErr;
      if (payload.cards.length) {
        const rows = payload.cards
          .filter(c => c.quantity > 0)
          .map(c => ({ deck_id: deckId, card_id: c.card_id, quantity: c.quantity }));
        if (rows.length) {
          const { error: insErr } = await supabase.from('deck_cards').insert(rows);
          if (insErr) throw insErr;
        }
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['deck-cards', vars.deckId] });
      qc.invalidateQueries({ queryKey: ['decks'] });
    },
  });
}
